import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import { API_URL } from '../apiConfig';


const ApiCourses = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Fetching data from the provided API endpoint
        console.log('ApiCourses: Fetching from:', API_URL);
        fetch(API_URL)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                // Ensure data is an array
                const courseList = Array.isArray(data) ? data : (data.courses || []);
                setCourses(courseList);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching courses:', err);
                setError(err.message);
                setLoading(false);
                
                // For development/demo purposes if the live API is not reachable
                if (process.env.NODE_ENV === 'development') {
                    setCourses([
                        { 
                            course_title: "COC Welding Level 1", 
                            course_category: "Others", 
                            course_level: "Level 1",
                            course_fee: "300.00",
                            duration: 1,
                            duration_unit: "days"
                        },
                        { 
                            course_title: "COC Welding Level 2", 
                            course_category: "Others", 
                            course_level: "Level 2",
                            course_fee: "900.00",
                            duration: 3,
                            duration_unit: "days"
                        }
                    ]);
                }
            });
    }, []);

    const formatFee = (fee) => {
        if (fee === null || fee === undefined || fee === '') return 'N/A';
        const normalized = typeof fee === 'string' ? fee.replace(/[^0-9.]/g, '') : fee;
        const amount = Number(normalized);
        return Number.isNaN(amount) ? fee : amount.toLocaleString('en-SG', { style: 'currency', currency: 'SGD' });
    };

    const formatLevel = (course) => {
        if (course.course_level) return course.course_level;
        if (course.course_category) return course.course_category;
        return 'Level info unavailable';
    };


    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <SEO 
                title="API Courses" 
                description="View our latest courses fetched directly from our training management system."
            />
            <Navbar />
            
            {/* Header Section */}
            <div className="bg-[#0b9949] pt-40 pb-20">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-6xl font-extrabold text-white mb-6"
                    >
                        Available Courses
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-green-100 text-lg md:text-xl max-w-3xl mx-auto"
                    >
                        Real-time course listings from our training database. Get the latest levels and fees for all our professional programs.
                    </motion.p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 -mt-10 pb-24">
                {loading ? (
                    <div className="bg-white rounded-3xl shadow-xl p-20 flex flex-col items-center justify-center">
                        <div className="w-16 h-16 border-4 border-green-100 border-t-[#0b9949] rounded-full animate-spin mb-6"></div>
                        <p className="text-gray-500 font-medium animate-pulse">Syncing with database...</p>
                    </div>
                ) : error && courses.length === 0 ? (
                    <div className="bg-white rounded-3xl shadow-xl p-16 text-center border-2 border-red-50">
                        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Connection Error</h2>
                        <p className="text-gray-600 mb-8">We couldn't connect to the course API. Please ensure your local server is running at localhost.</p>
                        <button 
                            onClick={() => window.location.reload()}
                            className="px-8 py-3 bg-[#0b9949] text-white rounded-full font-bold hover:bg-[#087a38] transition-all shadow-lg hover:shadow-green-200/50"
                        >
                            Retry Connection
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {courses.map((course, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-100 group flex flex-col h-full"
                            >
                                <div className="p-1 bg-gradient-to-r from-green-400 to-green-600"></div>
                                <div className="p-8 flex-grow">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="px-4 py-1.5 bg-green-50 text-[#0b9949] text-xs font-black rounded-lg uppercase tracking-widest border border-green-100">
                                            {course.duration} {course.duration_unit}
                                        </div>
                                        <div className="text-right">
                                            <span className="text-sm text-gray-400 block font-medium">Fee</span>
                                            <span className="text-2xl font-black text-gray-900">${course.course_fee}</span>
                                        </div>
                                    </div>
                                    
                                    <h2 className="text-2xl font-extrabold text-gray-800 mb-4 group-hover:text-[#0b9949] transition-colors leading-tight">
                                        {course.course_title}
                                    </h2>
                                    
                                    <div className="grid grid-cols-2 gap-4 text-gray-500 mb-8 p-3 bg-gray-50 rounded-xl">
                                        <div className="rounded-xl p-3 bg-white shadow-sm border border-gray-100">
                                            <span className="text-[10px] uppercase font-bold text-gray-400 block tracking-wider">Level</span>
                                            <span className="text-sm font-semibold text-gray-700">{formatLevel(course)}</span>
                                        </div>
                                        <div className="rounded-xl p-3 bg-white shadow-sm border border-gray-100">
                                            <span className="text-[10px] uppercase font-bold text-gray-400 block tracking-wider">Course Fee</span>
                                            <span className="text-sm font-semibold text-gray-700">{formatFee(course.course_fee)}</span>
                                        </div>
                                    </div>

                                    <Link to={`/course-details/api-${index}`} className="w-full">
                                        <button className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-[#0b9949] transition-all duration-300 shadow-lg flex items-center justify-center gap-3 group/btn">
                                            View Details & Enroll
                                            <svg className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </button>
                                    </Link>
                                </div>
                                <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                                    <div className="flex -space-x-2">
                                        {[1,2,3].map(i => (
                                            <div key={i} className="w-6 h-6 rounded-full border-2 border-white bg-gray-200"></div>
                                        ))}
                                    </div>
                                    <span className="text-xs text-gray-400 font-medium italic">Join 500+ active students</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default ApiCourses;
